import copy from "copy-to-clipboard";
import { enqueueSnackbar } from "notistack";

const isIOSDevice = () => {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const hasTouchOnMac =
    platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;

  return /iPad|iPhone|iPod/i.test(ua) || hasTouchOnMac;
};

const showManualCopyModal = (text) => {
  if (typeof document === "undefined") return;

  const existing = document.getElementById("manual-copy-modal");
  if (existing) existing.remove();

  const overlay = document.createElement("div");
  overlay.id = "manual-copy-modal";
  overlay.style.position = "fixed";
  overlay.style.inset = "0";
  overlay.style.background = "rgba(0, 0, 0, 0.45)";
  overlay.style.display = "flex";
  overlay.style.alignItems = "center";
  overlay.style.justifyContent = "center";
  overlay.style.zIndex = "99999";
  overlay.style.padding = "16px";

  const modal = document.createElement("div");
  modal.style.width = "min(680px, 100%)";
  modal.style.maxHeight = "90vh";
  modal.style.background = "#fff";
  modal.style.borderRadius = "12px";
  modal.style.padding = "16px";
  modal.style.display = "flex";
  modal.style.flexDirection = "column";
  modal.style.gap = "12px";

  const title = document.createElement("div");
  title.textContent = "Kopiranje teksta (dug pritisak pa Copy)";
  title.style.fontWeight = "600";
  title.style.fontSize = "16px";

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.readOnly = true;
  textArea.style.width = "100%";
  textArea.style.minHeight = "240px";
  textArea.style.maxHeight = "60vh";
  textArea.style.padding = "10px";
  textArea.style.fontSize = "14px";
  textArea.style.fontFamily = "inherit";
  textArea.style.border = "1px solid #d0d5dd";
  textArea.style.borderRadius = "8px";
  textArea.style.resize = "vertical";

  const actions = document.createElement("div");
  actions.style.display = "flex";
  actions.style.justifyContent = "flex-end";
  actions.style.gap = "8px";

  const copyButton = document.createElement("button");
  copyButton.type = "button";
  copyButton.textContent = "Kopiraj";
  copyButton.style.padding = "8px 12px";
  copyButton.style.border = "1px solid #111827";
  copyButton.style.borderRadius = "8px";
  copyButton.style.background = "#111827";
  copyButton.style.color = "#fff";
  copyButton.style.cursor = "pointer";

  const close = () => overlay.remove();

  copyButton.onclick = async () => {
    try {
      if (
        typeof window !== "undefined" &&
        window.isSecureContext &&
        navigator?.clipboard?.writeText
      ) {
        await navigator.clipboard.writeText(text);
        enqueueSnackbar("Uspešno kopirano u clipboard!", {
          variant: "success",
        });
        close();
        return;
      }
    } catch (err) {
      console.warn("Clipboard API copy failed on iOS modal:", err);
    }

    textArea.focus();
    textArea.select();

    try {
      const success = copy(text);
      if (success) {
        enqueueSnackbar("Uspešno kopirano u clipboard!", {
          variant: "success",
        });
        close();
        return;
      }
    } catch (err) {
      console.warn("Fallback copy failed on iOS modal:", err);
    }

    enqueueSnackbar("Selektovan je tekst. Dodirni Kopiraj iz menija uređaja.", {
      variant: "info",
    });
  };
  overlay.onclick = (event) => {
    if (event.target === overlay) close();
  };

  actions.appendChild(copyButton);
  modal.appendChild(title);
  modal.appendChild(textArea);
  modal.appendChild(actions);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  textArea.focus();
  textArea.select();
};

export const formatRouteGroupForClipboard = (group) => {
  if (!group) return "Nema dostupnih podataka.";

  const { title, routes = [], externalArrival = [] } = group;

  let output = "";
  let totalPassengers = 0;
  const groupTitle = title || "Neimenovana rezervacija";
  output += `${groupTitle}\n`;
  routes.forEach((route, index) => {
    const adult = route.numberOfAdultPassengers ?? 0;
    const child = route.numberOfChildPassengers ?? 0;
    const routeTotal = adult + child;
    totalPassengers += routeTotal;

    // Naslov

    output += `----------------------------------------\n`;

    // Broj osoba
    let peopleText = adult.toString();
    if (child > 0) peopleText += `+${child}`;
    output += `${peopleText} OSOBA\n\n`;

    // Putnici
    if (Array.isArray(route.passengers) && route.passengers.length > 0) {
      output += `Putnici:\n`;
      route.passengers.forEach((p, idx) => {
        output += `  - ${p.fullName || ""}\n`;
        if (p.address) output += `    Adresa: ${p.address}\n`;
        if (p.contactPhoneNumber)
          output += `    Kontakt: ${p.contactPhoneNumber}\n`;
      });
    } else {
      output += "Nema podataka o putnicima.\n\n";
    }

    // Autosedišta
    const seats = [];
    if (route.largeSeat > 0)
      seats.push(`${route.largeSeat} velik${route.largeSeat < 4 ? "a" : "ih"}`);
    if (route.mediumSeat > 0)
      seats.push(
        `${route.mediumSeat} srednj${route.mediumSeat < 4 ? "a" : "ih"} `
      );
    if (route.smallSeat > 0)
      seats.push(`${route.smallSeat} mal${route.smallSeat < 4 ? "a" : "ih"}`);
    if (seats.length > 0) output += `Autosedišta (${seats.join(", ")})\n`;

    // Napomena vozača
    if (route.driverNote) output += `Napomena: ${route.driverNote}\n`;

    // Kontakt
    if (route.contactPhone) {
      let contactType = "";
      if (route.contactType.toLowerCase().includes("wcp")) {
        contactType = "WhatsApp";
      } else if (route.contactType.toLowerCase().includes("vib")) {
        contactType = "Viber";
      } else {
        contactType = "";
      }
      output += `Kontakt telefon: ${route.contactPhone} ${contactType}\n`;
    }

    // if(route.flightTime)
    // output += `Vreme leta: ${route.flightTime}\n`;

    if (route.travelType === 1 && route.isAnotherWay === false) {
      output += `Let ${route.flightTime} ${route.address}\n`;
    }

    if (route.travelType === 2 && route.isAnotherWay === false) {
      output += `Adresa ${route.flightTime} ${route.address}\n`;
    }

    if (route.price) output += `Cena: ${route.price}\n`;
    if(route.destinationTime) output += `Vreme polaska: ${route.destinationTime}\n`;

    if (route.travelType === 1 && route.isAnotherWay === true)
      output += `${route.destinationTime} Let ${route.flightTime ?? ""} ${route.address ?? ""}\n`;
    if (route.travelType === 2 && route.isAnotherWay === true)
      output += `${route.destinationTime} Adresa ${route.flightTime ?? ""} ${route.address ?? ""}\n`;

    output += "\n"; // razmak između ruta
  });

  // Eksterni dovodi
  if (Array.isArray(externalArrival) && externalArrival.length > 0) {
    externalArrival.forEach((ext, idx) => {
      if (ext.numberOfPassengers > 0) {
        output += `----------------------------------------\n`;
        output += `${ext.numberOfPassengers} OSOBA DOVODI ${ext.driver} ${ext.vehicle} \n`;
      }
    });
  } else {
    output += "\n";
  }

  const externalArrivalTotal = externalArrival.reduce(
    (sum, ext) => sum + (ext.numberOfPassengers || 0),
    0
  );
  output += `----------------------------------------\n`;
  output += `UKUPNO ${totalPassengers + externalArrivalTotal} OSOBA\n`;

  console.log(output);

  return output;
};

export const copyTextToClipboard = (text) => {
  if (isIOSDevice()) {
    showManualCopyModal(String(text || ""));
    enqueueSnackbar(
      "Na iOS uređajima koristi dug pritisak u polju pa Copy.",
      { variant: "info" },
    );
    return;
  }

  try {
    const success = copy(text); // vraća true ako je uspešno kopirano
    if (success) {
      enqueueSnackbar("Uspešno kopirano u clipboard!", { variant: "success" });
    } else {
      enqueueSnackbar("Greška pri kopiranju u clipboard.", {
        variant: "error",
      });
    }
  } catch (err) {
    console.error("Greška pri kopiranju u clipboard:", err);
    enqueueSnackbar("Greška pri kopiranju u clipboard.", { variant: "error" });
  }
};
